import { useCallback, useEffect, useMemo, useState } from 'react';
import { SERVER_URL } from '../config';
import { formatPlanPrice, getCurrencySymbol, getPlanPrice } from '../utils/pricing';

/* India sees INR, everywhere else sees USD — same rule as the product site.
   The product app resolves this server-side via /api/v1/public/razorpay-billing/detect-currency.
   This is a static marketing site, so we resolve it instantly from the browser's own
   timezone/locale (no network, no flash of the wrong price) and only then, if a backend
   URL is configured, confirm against that same endpoint. */

/* Confirms the region server-side against the same endpoint the product app
   uses. The local timezone guess only exists to avoid a flash of the wrong
   price; it is NOT good enough to choose a payment gateway with, because a
   VPN or an unusual timezone would route an Indian customer to Stripe. Sign-up
   therefore waits for `resolved` before picking a gateway, exactly as the
   host's Register.js does. */
export const CURRENCY_API_BASE = SERVER_URL;

const CACHE_KEY = 'teamgrid_detected_currency';
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes — matches the product app

const IN_ZONES = ['asia/kolkata', 'asia/calcutta'];

function readCache(key, ttl) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const { currency, timestamp } = JSON.parse(raw);
    if (ttl && Date.now() - timestamp > ttl) return null;
    return currency || null;
  } catch {
    return null;
  }
}

function writeCache(key, currency) {
  try {
    sessionStorage.setItem(key, JSON.stringify({ currency, timestamp: Date.now() }));
  } catch {
    /* private mode — detection just re-runs next page */
  }
}

/** Best-effort guess with zero network: IANA timezone first, then locale region. */
export function guessCurrency() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz && IN_ZONES.includes(tz.toLowerCase())) return 'INR';
  } catch {
    /* fall through */
  }
  try {
    const locales = navigator.languages?.length ? navigator.languages : [navigator.language];
    if (locales.some((l) => typeof l === 'string' && /-IN$/i.test(l))) return 'INR';
  } catch {
    /* fall through */
  }
  return 'USD';
}

export default function useCurrency() {
  const [currency, setCurrency] = useState(
    () => readCache(CACHE_KEY, CACHE_DURATION) || guessCurrency(),
  );
  /* true once we've either read a cached/explicit choice or finished asking the
     server. Callers that spend money must wait for this. */
  const [resolved, setResolved] = useState(
    () => !!readCache(CACHE_KEY, CACHE_DURATION),
  );

  useEffect(() => {
    const base = CURRENCY_API_BASE;
    if (!base) {
      writeCache(CACHE_KEY, currency);
      setResolved(true);
      return undefined;
    }
    const ctl = new AbortController();
    fetch(`${String(base).replace(/\/$/, '')}/api/v1/public/razorpay-billing/detect-currency`, {
      signal: ctl.signal,
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        const detected = j?.data?.currency;
        if (detected === 'INR' || detected === 'USD') {
          setCurrency(detected);
          writeCache(CACHE_KEY, detected);
        }
      })
      .catch(() => {
        /* offline or CORS — the timezone guess already stands */
      })
      .finally(() => {
        /* resolved even on failure, so checkout can't hang forever waiting */
        setResolved(true);
      });
    return () => ctl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const symbol = useMemo(() => getCurrencySymbol(currency), [currency]);
  // interval is optional and defaults to monthly, so existing callers are unaffected.
  const price = useCallback((plan, interval) => getPlanPrice(plan, currency, interval), [currency]);
  const formatPrice = useCallback((plan, interval) => formatPlanPrice(plan, currency, interval), [currency]);

  return { currency, resolved, symbol, price, formatPrice };
}
